import { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "@simplybusiness/mobius";
import {
  PricingNudge,
  type PricingNudgeCopyVariant,
} from "../../components/PricingNudge";
import type { InterventionProps } from "../../types";
import { convertNudgeToMSM } from "../../utils/utils";
import { IP_WIDGET_ORIGIN_SITE, IP_WIDGET_SCRIPT_URLS } from "../../constants";

declare global {
  interface Window {
    showIndicativePricingPopup: () => void;
  }
}

/**
 * Determines which copy variant to use based on component_names array
 * Uses explicit matching to select variant A or B, defaults to default copy
 */
const selectCopyVariant = (
  componentNames?: string[],
): PricingNudgeCopyVariant => {
  if (!componentNames || componentNames.length === 0) {
    return "default";
  }

  // Explicit matching for variant A
  if (componentNames.includes("indicative-pricing-copy-variant-a")) {
    return "variantA";
  }

  // Explicit matching for variant B
  if (componentNames.includes("indicative-pricing-copy-variant-b")) {
    return "variantB";
  }

  // Default fallback
  return "default";
};

export const PricingNudgeIntervention = (props: InterventionProps) => {
  const { site, vertical, fireEvent, data } = props;
  const scriptLoaded = useRef(false); //Whether the IP widget script has been loaded
  const modalViewed = useRef(false); //Whether the modal has been viewed
  const [showModal, setShowModal] = useState(false); //Whether the Nudge banner should show (false until the script loads)
  const [shownIPWidget, setShownIPWidget] = useState(false); //Whether the IP widget popup modal was requested.

  //Handles loading the IPW external script when the component mounts
  useEffect(() => {
    if (scriptLoaded.current) return;

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.className = "ip-widget-loader";
    script.src = IP_WIDGET_SCRIPT_URLS();
    script.defer = true;
    script.onload = () => setShowModal(true);
    script.onerror = () => fireEvent!("hydra_ip_script_failed_to_load");

    document.head.appendChild(script);

    scriptLoaded.current = true;

    return () => {
      document.head.removeChild(script);
    };
  }, [fireEvent]);

  //Callback for the "Show IPW" CTA button
  const showIPWidgetCallback = useCallback(() => {
    fireEvent!("hydra_ip_cta_clicked");

    //If the IPW function didn't load yet or load properly, instead of opening the IPW,
    //Fire an event so we know that it didn't load.
    if (
      !window.showIndicativePricingPopup ||
      typeof window.showIndicativePricingPopup !== "function"
    ) {
      fireEvent!("hydra_ip_cta_not_loaded_when_clicked");
      return;
    }

    window.showIndicativePricingPopup();

    setShownIPWidget(true);
    setShowModal(false);
  }, [setShownIPWidget, fireEvent]);

  /* --------------------------------------------------
    UseEffects to handle the intervention lifecycle
  -------------------------------------------------- */
  useEffect(() => {
    if (site == "moneysupermarket") {
      convertNudgeToMSM();
    }
  }, [site]);

  useEffect(() => {
    if (showModal && !modalViewed.current) {
      fireEvent!("hydra_ip_modal_viewed");
      modalViewed.current = true;
    }
  }, [showModal, fireEvent]);

  useEffect(() => {
    const handleIPWidgetClosed = (_: Event) => {
      if (shownIPWidget) {
        props.onDismiss?.();
      }
    };

    window.addEventListener("embedded-ip-widget-closed", handleIPWidgetClosed);

    return () => {
      window.removeEventListener(
        "embedded-ip-widget-closed",
        handleIPWidgetClosed,
      );
    };
  }, [shownIPWidget, props]);

  /* ----------------------
    Render
  ---------------------- */
  //NB: Header contains a nonbreaking space to force the close button to align right
  return (
    <div id="priceNudgeIntervention">
      <div
        id="sb-ip-app"
        data-display-mode="noBanner"
        data-do-not-redirect="true"
        data-origin-override={IP_WIDGET_ORIGIN_SITE(props.site)}
        data-vertical={vertical}
      />
      <Modal
        data-testid="pricing-nudge-intervention-modal"
        isOpen={showModal}
        onClose={() => {
          if (!shownIPWidget) {
            //The IP widget isn't showing, so the user must have explicitly closed the outer modal.
            fireEvent!("hydra_ip_closed_banner");
            setShowModal(false);
            props.onDismiss?.();
          }
        }}
        animation="fade"
        closeLabel="Close"
      >
        <Modal.Header>{"\u00A0"}</Modal.Header>
        <Modal.Content>
          <PricingNudge
            onPriceEstimateButtonClickedCallback={showIPWidgetCallback}
            vertical={vertical}
            copyVariant={selectCopyVariant(data?.component_names)}
          />
        </Modal.Content>
      </Modal>
    </div>
  );
};
